<script setup lang="ts">
import { computed } from "vue";
import { UiIcon, UiText } from "@shopexperts/shared";
import { useExpertQuoteFeeEstimate, useQuoteFeeEstimateCollapsed } from "@/app/composables/wallet";
import WalletTransactionBreakdownList from "@/components/expert/wallet/WalletTransactionBreakdownList.vue";
import { formatCurrency } from "@/v2Projects/utils/display";
import { quoteCopy } from "@/v2Projects/utils/quoteCopy";

const props = withDefaults(
  defineProps<{
    actingOrgId: number;
    amountCents: number | null;
    currency: string;
    enabled?: boolean;
  }>(),
  {
    enabled: true,
  },
);

const { collapsed, toggle: toggleCollapsed } = useQuoteFeeEstimateCollapsed();
const regionId = `quote-fee-estimate-${Math.random().toString(36).slice(2)}`;

const isVisible = computed(
  () => props.enabled && props.amountCents !== null && props.amountCents > 0,
);

const {
  estimate,
  breakdownRows,
  providerFeeLines,
  isPending,
} = useExpertQuoteFeeEstimate({
  actingOrgId: computed(() => props.actingOrgId),
  amountCents: computed(() => props.amountCents),
  currency: computed(() => props.currency),
  enabled: isVisible,
});

const formattedNetEarnings = computed(() => {
  if (estimate.value?.net_amount_cents == null) return null;
  return formatCurrency(estimate.value.net_amount_cents, estimate.value.currency);
});

const detailBreakdownRows = computed(() =>
  breakdownRows.value.filter((row) => row.key !== "net"),
);

const hasDetailBreakdown = computed(() => detailBreakdownRows.value.length > 0);
</script>

<template>
  <div v-if="isVisible" class="flex flex-col border-t border-dashed border-gray-200 pt-5">
    <button
      type="button"
      class="group flex w-full cursor-pointer items-baseline justify-between gap-6 text-left"
      :aria-expanded="!collapsed"
      :aria-controls="regionId"
      @click="toggleCollapsed"
    >
      <span class="flex min-w-0 items-center gap-1">
        <UiText
          as="span"
          variant="caption"
          :text="quoteCopy.feeEstimateNetLabel"
          class="font-semibold text-se-dark"
        />
        <UiIcon
          name="chevron-down"
          class="h-3 w-3 shrink-0 text-se-dark transition-transform duration-300"
          :class="collapsed ? '-rotate-90' : 'rotate-0'"
        />
      </span>
      <UiText
        v-if="formattedNetEarnings"
        as="span"
        variant="h4"
        :text="formattedNetEarnings"
        class="font-semibold tabular-nums text-se-dark"
      />
      <span
        v-else
        class="flex items-center gap-2"
        role="status"
        aria-live="polite"
      >
        <span class="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-se-dark border-t-transparent" />
        <UiText
          as="span"
          variant="p-small"
          :text="quoteCopy.feeEstimateLoading"
          class="text-se-dark"
        />
      </span>
    </button>

    <div
      :id="regionId"
      class="grid transition-[grid-template-rows] duration-300 ease-out"
      :class="collapsed ? 'grid-rows-[0fr]' : 'grid-rows-[1fr]'"
    >
      <div class="min-h-0 overflow-hidden">
        <div class="flex flex-col gap-2.5 pt-2.5">
          <div v-if="hasDetailBreakdown" class="flex flex-col gap-2">
            <UiText
              as="span"
              variant="caption"
              :text="quoteCopy.feeEstimateHeading"
              class="font-semibold text-se-dark"
            />
            <WalletTransactionBreakdownList
              :rows="detailBreakdownRows"
              :provider-fee-lines="providerFeeLines"
              :currency="currency"
            />
          </div>

          <div class="flex items-start gap-2">
            <UiIcon name="info-circle" class="mt-0.5 h-3.5 w-3.5 shrink-0 text-se-dark" />
            <UiText
              as="p"
              variant="caption-normal"
              :text="quoteCopy.feeEstimateDisclaimer"
              class="text-se-dark"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
